import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { submitInvitationRSVP } from "../../utils/invitationsApi";

const InvitationRSVPForm = ({
  slug,
  defaultName = "",
  defaultGuestId = "",
  disabled = false,
  disabledReason,
  primaryColor,
  accentColor,
  hideHeading = false,
  fontStyles = {},
}) => {
  const storageKey = useMemo(() => `invitation_rsvp_${slug}`, [slug]);

  const [form, setForm] = useState({
    name: defaultName,
    response: "yes",
    message: "",
    invitation_guest_id: defaultGuestId,
    number_of_people: "1",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [answered, setAnswered] = useState(false);

  useEffect(() => {
    if (disabled) {
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.answered) {
        setAnswered(true);
        setSuccess(true);
        setForm((prev) => ({
          ...prev,
          name: parsed.name || prev.name,
          response: parsed.response || prev.response,
          message: parsed.message || "",
          number_of_people:
            parsed.number_of_people ||
            parsed.guest_count ||
            prev.number_of_people ||
            "1",
        }));
      }
    } catch (err) {
      console.warn("Unable to read RSVP cache", err);
    }
  }, [storageKey, disabled]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (disabled || answered) {
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(false);

    const payload = {
      name: form.name.trim(),
      response: form.response.toLowerCase(), // must be in lowercase - yes, no, maybe
      message: form.message.trim() || null,
      number_of_people: Number(form.number_of_people) || 0,
    };

    if (form.invitation_guest_id) {
      payload.invitation_guest_id = form.invitation_guest_id;
    }

    try {
      await submitInvitationRSVP(slug, payload);
      setSuccess(true);
      setAnswered(true);

      if (!disabled) {
        const cache = {
          answered: true,
          name: payload.name,
          response: payload.response,
          message: payload.message || "",
          number_of_people: String(payload.number_of_people ?? "1"),
          guest_count: String(payload.number_of_people ?? "1"),
          timestamp: new Date().toISOString(),
        };
        try {
          localStorage.setItem(storageKey, JSON.stringify(cache));
        } catch (storageError) {
          console.warn("Unable to persist RSVP cache", storageError);
        }
      }

      setForm((prev) => ({
        ...prev,
        message: payload.message || "",
      }));
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно испраќање на RSVP.");
    } finally {
      setSubmitting(false);
    }
  };

  const isLocked = disabled || answered;

  const headingFont = fontStyles.subtitle || fontStyles.title || {};
  const bodyFont = fontStyles.body || {};
  const buttonFont = fontStyles.subtitle || fontStyles.title || {};
  const textFieldFontSx = {
    "& .MuiInputBase-input": {
      ...bodyFont,
    },
    "& .MuiInputLabel-root": {
      ...bodyFont,
    },
  };

  return (
    <form onSubmit={handleSubmit} className="invitation-rsvp-form">
      <Stack spacing={2}>
        {!hideHeading && (
          <Typography
            variant="h5"
            component="h3"
            sx={{ ...headingFont }}
          >
            RSVP
          </Typography>
        )}

        {error && <Alert severity="error">{error}</Alert>}
        {disabledReason && !success && (
          <Alert
            severity="info"
            sx={{
              backgroundColor: "rgba(227, 95, 135, 0.12)",
              color: "var(--primary-color)",
              border: "1px solid rgba(227, 95, 135, 0.4)",
            }}
          >
            {disabledReason}
          </Alert>
        )}
        {success && (
          <Alert severity="success">
            Ви благодариме! Вашиот одговор е евидентиран.
          </Alert>
        )}

        <TextField
          label="Вашето име"
          value={form.name}
          onChange={handleChange("name")}
          required
          disabled={isLocked}
          sx={textFieldFontSx}
        />

        <TextField
          select
          label="Ќе присуствувам"
          value={form.response}
          onChange={handleChange("response")}
          disabled={isLocked}
          sx={textFieldFontSx}
        >
          <MenuItem value="yes" style={{ ...bodyFont }}>
            Да
          </MenuItem>
          <MenuItem value="no" style={{ ...bodyFont }}>
            Не
          </MenuItem>
          <MenuItem value="maybe" style={{ ...bodyFont }}>
            Можеби
          </MenuItem>
        </TextField>

        <TextField
          select
          label="Вкупно гости (вклучувајќи ве)"
          value={form.number_of_people}
          onChange={handleChange("number_of_people")}
          disabled={isLocked}
          sx={textFieldFontSx}
        >
          {[1, 2, 3, 4, 5, 6].map((count) => (
            <MenuItem value={String(count)} key={count} style={{ ...bodyFont }}>
              {count === 1 ? "Само јас" : count}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Порака (опционално)"
          value={form.message}
          onChange={handleChange("message")}
          multiline
          minRows={3}
          disabled={isLocked}
          placeholder={answered && !form.message ? "Без порака" : undefined}
          sx={textFieldFontSx}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={isLocked || submitting}
          sx={{
            backgroundColor: accentColor || "var(--primary-color)",
            color: primaryColor || undefined,
            "&:hover": {
              backgroundColor: accentColor || "var(--primary-color)",
              filter: "brightness(0.95)",
            },
            opacity: isLocked || submitting ? 0.6 : 1,
            ...buttonFont,
          }}
        >
          {isLocked
            ? "RSVP е зачуван"
            : submitting
            ? "Се испраќа..."
            : "Испрати RSVP"}
        </Button>
      </Stack>
    </form>
  );
};

export default InvitationRSVPForm;
